import type {
  LegacyAsset,
  V2Portfolio,
  V2PortfolioAsset,
  WalletAsset,
} from "../../types/api";
import {
  getRate,
  getTradeRate,
  getUserOrders,
} from "../../services/tradingService";
import { getTradingAssets } from "../../services/assetService";
import {
  getTransactionHistory,
  getUserTransactionHistory,
  getMyFiatTransactions,
  getUserPayTransactions,
} from "../../services/transactionService";
import {
  getPortfolio,
  getPortfolioEstimateAssets,
  getPortfolioPerformance,
} from "../../services/walletService";
import { useCanLoadPortfolioEstimate } from "./portfolioEstimateGate";
import { getMarketAssets, getPriceHistory } from "../../services/marketService";
import { getCryptoAddresses as getWalletAddresses } from "../../services/addressBookService";
import {
  getAssets,
  getAssetWallet,
  getNet,
  getWalletAssets,
} from "../../services/walletService";
import {
  getEightyEightBank,
  getBanks,
  getBankAccounts,
  getUserBanking,
  getUserBank,
} from "../../services/bankingService";
import type { BankAccountListResponse } from "../../services/bankingService";
import { getFees } from "../../services/transactionService";
import {
  getAssetPairs,
  getAllTradablePairsOptimized,
} from "../../services/assetService";
import { getAllAssets as getAllTradingAssets } from "../../services/assetService";
import { getAllTradingData } from "../../services/assetService";
import { getBalances as getWalletAssetsData } from "../../services/walletService";

import {
  getFaqCategories,
  getFaqs,
  searchFaq,
} from "../../services/supportService";
import {
  getNotifications,
  formatNotification,
} from "../../services/notificationService";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMemo, useEffect } from "react";
import { getUser } from "../../services/userService";
import { get, updatePatch } from "../../services/apiService";
import { useTransactionTrackingStore } from "../../store/useTransactionTrackingStore";
import { getAllAssets as getAssetsWithChains } from "../../services/assetService";
import { useAuth } from "../../context/AuthContext";
import {
  TRANSACTION_HISTORY_FETCH_LIMIT,
  TRANSACTION_HISTORY_PAGE_SIZE,
} from "../../constants/transactionHistoryPagination";
import {
  fetchAllUserTransactionHistory,
  fetchBusinessTransactionHistoryPage,
  fetchUserTransactionHistoryPage,
  type UserTransactionHistoryPageResult,
  type UserTransactionHistoryQuery,
} from "../../utils/userTransactionHistoryFetch";
import { useCurrencySelection } from "../../context/CurrencySelectionContext";
import type { WalletQueryScope } from "../../context/WalletScopeContext";
import {
  formatDisplayFiat,
  getCurrencyPrefix,
  type DisplayCurrency,
} from "../../utils/displayCurrency";
import {
  createDepositQuote,
  getApplication,
  getAvalableAmount,
  getExistingApplication,
  getInvestmentPayWith,
  getInvestPortfolio,
  getInvestProduct,
  getInvestProducts,
  getLedgerEntries,
  getPendingLedgers,
  getProductTransactionHistory,
  getSingleInvestment,
  investPerformanceGraph,
} from "../../services/investService";

import {
  getAutoStackStats,
  getAutoStackTemplates,
  getUserAutoStacks,
  getUserSingleAutoStack,
  getAutoStackPurchases,
  getAllAutoStackPurchases,
  computeSingleAutoStackStats,
} from "../../services/autostackService";
import { useActiveBusiness } from "./dposQueries";
import {
  getZar8YieldConfig,
  getZar8YieldEarnings,
  getZar8YieldHistory,
  getZar8YieldPosition,
  getZar8YieldPositions,
} from "../../services/zar8YieldService";
import {
  getBusinessZar8Position,
  getBusinessZar8Positions,
  getBusinessZar8YieldConfig,
  getBusinessZar8YieldEarnings,
  getBusinessZar8YieldHistory,
} from "../../services/businessZar8YieldService";
import {
  getBankReference as getBankTransferReference,
  resolveDefaultBankAccountId,
} from "../../services/bankingService";
import {
  getAccountId,
  type SubAccount,
} from "../../services/subAccountService";
import { getActiveTransactions as getNormalizedActiveTransactions } from "../../services/transactionService";
import { getAccountId as getStoredAccountId } from "../../context/AuthActions";
import { formatCurrencyValue } from "../../utils/numberFormatter";

interface LegacyEnvelope<T> {
  data?: {
    data?: T;
    status?: boolean;
  };
  status?: boolean;
}

function unwrapLegacyEnvelope<T>(payload: unknown): T | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const typedPayload = payload as LegacyEnvelope<T> & T;

  if (
    "data" in typedPayload &&
    typedPayload.data &&
    typeof typedPayload.data === "object" &&
    "data" in typedPayload.data
  ) {
    return typedPayload.data.data ?? null;
  }

  return typedPayload as T;
}

function parseNumericLike(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }

    const sanitized = value.replace(/,/g, "").replace(/[^\d.-]/g, "");
    const formattedParsed = parseFloat(sanitized);
    return Number.isFinite(formattedParsed) ? formattedParsed : 0;
  }

  if (
    value &&
    typeof value === "object" &&
    "$numberDecimal" in (value as Record<string, unknown>)
  ) {
    const decimalValue = (value as { $numberDecimal?: string }).$numberDecimal;
    const parsed = decimalValue ? parseFloat(decimalValue) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function deriveAssetEstimatedPrice(asset: Record<string, unknown>): number {
  const directPrice = parseNumericLike(asset.assetEstimatedPrice);
  if (directPrice > 0) {
    return directPrice;
  }

  const availableAmount =
    parseNumericLike(asset.availableAmount) ||
    parseNumericLike(asset.available);
  const valueInZar = parseNumericLike(asset.valueInZar);

  if (availableAmount > 0 && valueInZar > 0) {
    return valueInZar / availableAmount;
  }

  const buyRate = parseNumericLike(asset.buyRate);
  if (buyRate > 0) {
    return buyRate;
  }

  const sellRate = parseNumericLike(asset.sellRate);
  if (sellRate > 0) {
    return sellRate;
  }

  return 0;
}

function normalizePortfolioAsset(asset: V2PortfolioAsset): V2PortfolioAsset {
  const assetRecord = asset as V2PortfolioAsset & Record<string, unknown>;

  return {
    ...asset,
    available: parseNumericLike(asset.available),
    valueInUsdt:
      parseNumericLike(assetRecord.valueInUsdt) ||
      parseNumericLike(assetRecord.valueUSD),
    valueInZar:
      parseNumericLike(assetRecord.valueInZar) ||
      parseNumericLike(assetRecord.valueZAR),
    valueInLocalCurrency: parseNumericLike(assetRecord.valueInLocalCurrency),
    assetEstimatedPrice: deriveAssetEstimatedPrice(assetRecord),
    buyRate: parseNumericLike(assetRecord.buyRate),
    sellRate: parseNumericLike(assetRecord.sellRate),
  } as V2PortfolioAsset;
}

function normalizePortfolioPayload(
  payload: V2Portfolio | null | undefined,
  displayCurrency: DisplayCurrency = "ZAR"
): V2Portfolio | null {
  if (!payload) {
    return null;
  }

  const normalizedAssets = Array.isArray(payload.assets)
    ? payload.assets.map(normalizePortfolioAsset)
    : [];
  const payloadRecord = payload as V2Portfolio & Record<string, unknown>;
  const zarEstimate =
    parseNumericLike(payload.zarEstimate) ||
    parseNumericLike(payloadRecord.totalValueZAR);
  const usdtEstimate =
    parseNumericLike(payload.usdtEstimate) ||
    parseNumericLike(payloadRecord.totalValueUSD);
  const displayEstimate =
    displayCurrency === "USD" ? usdtEstimate : zarEstimate;

  return {
    ...payload,
    assets: normalizedAssets.map((asset) => {
      const assetRecord = asset as V2PortfolioAsset & Record<string, unknown>;
      const selectedValue =
        displayCurrency === "USD"
          ? parseNumericLike(assetRecord.valueInUsdt)
          : parseNumericLike(assetRecord.valueInZar);

      return {
        ...asset,
        valueInLocalCurrency: selectedValue,
        valueInLocalCurrencyFormatted: formatDisplayFiat(
          selectedValue,
          displayCurrency
        ),
        currencySymbol: getCurrencyPrefix(displayCurrency),
      };
    }),
    usdtEstimate,
    zarEstimate,
    portfolioEstimateRaw: displayEstimate,
    portfolioEstimate: formatDisplayFiat(displayEstimate, displayCurrency),
  };
}

function createEmptyFiatPortfolioAsset(
  currency: DisplayCurrency
): V2PortfolioAsset {
  return {
    symbol: currency,
    balance: "0",
    valueZAR: "0",
    valueUSD: "0",
    allocation: 0,
    type: "fiat",
    currencyType: "Fiat",
    asset: currency,
    assetName:
      currency === "USD" ? "United States Dollar" : "South African Rand",
    available: 0,
    availableAmount: 0,
    valueInUsdt: 0,
    valueInZar: 0,
    valueInLocalCurrency: 0,
    valueInLocalCurrencyFormatted: formatDisplayFiat(0, currency),
    currencySymbol: getCurrencyPrefix(currency),
  };
}

export const useFiatBalance = (selectedFiat: LegacyAsset | null) => {
  const { data: portfolioData } = usePortfolioEstimateAssets();
  const { selectedCurrency } = useCurrencySelection();

  return useQuery({
    queryKey: [
      "walletBalance",
      selectedFiat?._id,
      portfolioData?.timestamp,
      selectedCurrency,
    ],

    queryFn: async () => {
      if (!selectedFiat || !portfolioData) return null;

      const assets = portfolioData?.assets || [];
      const matchedAsset = assets.find(
        (asset: V2PortfolioAsset) =>
          (asset as Record<string, unknown>).assetId === selectedFiat._id ||
          asset._id === selectedFiat._id ||
          asset.symbol?.toUpperCase() === selectedFiat.asset?.toUpperCase()
      );

      const matchedRecord = (matchedAsset as Record<string, unknown>) ?? {};
      const availableAmountRaw =
        matchedRecord.availableAmount ??
        matchedRecord.available ??
        matchedAsset?.balance ??
        "0";
      const availableAmount =
        typeof availableAmountRaw === "number"
          ? availableAmountRaw
          : parseFloat(
              String(availableAmountRaw)
                .replace(/^\s*[A-Z]{2,8}\s*/i, "")
                .replace(/^\s*[R$]\s*/i, "")
                .replace(/\s/g, "")
                .replace(/,/g, "")
            ) || 0;
      const withdrawalMinAmountField = (
        selectedFiat as LegacyAsset & {
          withdrawalMinAmount?: { $numberDecimal?: string };
        }
      ).withdrawalMinAmount;
      const minWithdrawal = withdrawalMinAmountField?.$numberDecimal || "0";
      return {
        availableAmount: formatCurrencyValue(
          availableAmount,
          selectedFiat.asset,
          {
            currencyDisplay: "symbol",
          }
        ),
        minWithdrawal,
      };
    },
    enabled: !!selectedFiat && !!portfolioData,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
export const useFaqCategories = () => {
  return useQuery({
    queryKey: ["faqCategories"],
    queryFn: async () => {
      const categories = await getFaqCategories();
      return categories;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFaq = (
  categoryId: string,
  subCategoryId: string | null = null
) => {
  return useQuery({
    queryKey: ["faq", categoryId, subCategoryId],
    queryFn: async () => {
      const data = await getFaqs(1, 20, categoryId, subCategoryId ?? undefined);
      return data.faqs;
    },
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 5,
  });
};

interface FaqSearchOptions {
  category?: string;
  limit?: number;
  offset?: number;
}

export const useFaqSearch = (q: string, options: FaqSearchOptions = {}) => {
  const { category, limit = 10, offset = 0 } = options;
  const trimmed = (q || "").trim();
  return useQuery({
    queryKey: ["faqSearch", trimmed, category, limit, offset],
    queryFn: async () => searchFaq(trimmed, { category, limit, offset }),
    enabled: trimmed.length > 0,
    staleTime: 1000 * 60,
  });
};

export const useFiatAssets = () => {
  return useQuery({
    queryKey: ["fiatAssets"],
    queryFn: async () => {
      try {
        const data = await getAssets("Fiat");
        return unwrapLegacyEnvelope<LegacyAsset[]>(data) ?? [];
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useInvestProduct = (id: string) => {
  return useQuery({
    queryKey: ["investProduct", id],
    queryFn: async () => {
      try {
        const data = await getInvestProduct(id);
        return data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!id,
  });
};
export const useApplicationEntry = (applicationId: string) => {
  return useQuery({
    queryKey: ["applicationEntry", applicationId],
    queryFn: async () => {
      try {
        const data = await getApplication(applicationId);
        return data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!applicationId,
  });
};
export const useExistingApplication = (userId: string, productId: string) => {
  return useQuery({
    queryKey: ["existingApplication", userId, productId],
    queryFn: async () => {
      try {
        const data = await getExistingApplication(productId);
        return data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!userId && !!productId,
  });
};
export const useInvestGraph = (
  productId: string,
  principle: number,
  timeframe: string
) => {
  return useQuery({
    queryKey: ["investGraph", productId, principle, timeframe],
    queryFn: async () => {
      return investPerformanceGraph(productId, principle, timeframe);
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!productId && !!principle,
  });
};

export const useAssetNetwork = (chaiId?: string | null) => {
  return useQuery({
    queryKey: ["assetNetwork", chaiId],
    queryFn: async () => {
      if (!chaiId) return null;
      const response = await getNet(chaiId);
      return unwrapLegacyEnvelope<Record<string, unknown>>(response);
    },
    enabled: !!chaiId,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePortfolioEstimate = (
  userId: string,
  startDate: string,
  endDate: string,
  scope?: WalletQueryScope
) => {
  const scopeBusinessId = scope?.businessId || undefined;
  const canLoadPortfolioEstimate = useCanLoadPortfolioEstimate();

  return useQuery({
    queryKey: [
      "portfolioEstimate",
      userId,
      startDate,
      endDate,
      scopeBusinessId || "personal",
    ],
    queryFn: async () => {
      const response = await getPortfolioPerformance({
        startDate,
        endDate,
        ...(scopeBusinessId ? { businessId: scopeBusinessId } : {}),
      });

      return response?.data?.data || { graphData: [] };
    },
    enabled: canLoadPortfolioEstimate && !!userId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useSingleBank = (bid?: string | null) => {
  return useQuery({
    queryKey: ["singleBank", bid],
    queryFn: async () => {
      if (!bid) return [];
      return await getUserBank(bid);
    },
    enabled: !!bid,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUserBanking = (userId: string) => {
  return useQuery({
    queryKey: ["userBanking", userId],
    queryFn: async () => {
      const response = await getUserBanking(userId);
      if (Array.isArray(response)) {
        return response;
      }
      return response?.bankAccounts || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useInfiniteUserBanking = (userId: string) => {
  return useInfiniteQuery({
    queryKey: ["userBanking", userId, "infinite"],
    initialPageParam: 1,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      getBankAccounts(pageParam, 20),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    getNextPageParam: (lastPage: BankAccountListResponse) =>
      lastPage.page < Math.max(1, lastPage.totalPages ?? 1)
        ? lastPage.page + 1
        : undefined,
  });
};

const normalizePlatformBank = (
  raw: Record<string, unknown> | null | undefined
): Record<string, unknown> | null => {
  if (!raw || typeof raw !== "object") return null;

  const pickString = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const value = (raw as Record<string, unknown>)[key];
      if (typeof value === "string" && value.trim() !== "") {
        return value;
      }
    }
    return undefined;
  };

  return {
    ...raw,
    recipientName: pickString(
      "recipientName",
      "accountHolder",
      "accountName",
      "beneficiaryName"
    ),
    bank: pickString("bank", "bankName", "name"),
    accountNumber: pickString("accountNumber", "accNumber", "accountNo"),
    branch: pickString("branch", "branchCode", "universalBranchCode"),
    swift: pickString("swift", "swiftCode"),
    accountType: pickString("accountType", "type"),
  };
};

export const useEightyEightBanks = () => {
  return useQuery({
    queryKey: ["eightyEightBanks"],
    queryFn: async () => {
      try {
        const response = await getEightyEightBank();
        return normalizePlatformBank(
          response as Record<string, unknown> | null | undefined
        );
      } catch (error) {}
      return null;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useHas88PayHistory = (
  userId: string,
  businessId?: string | null
) => {
  const activeAccountId = getStoredAccountId() || "";

  return useQuery({
    queryKey: ["has88PayHistory", userId, activeAccountId, businessId],
    enabled: !!userId,
    queryFn: async () => {
      try {
        const response = await getUserPayTransactions(
          activeAccountId,
          businessId || undefined
        );
        const results =
          (response as { data?: { data?: { results?: unknown[] } } })?.data
            ?.data?.results || [];
        return results.length > 0;
      } catch (error) {
        return false;
      }
    },
    staleTime: 1000 * 60 * 30,
  });
};

export const useAllBanks = () => {
  return useQuery({
    queryKey: ["allBanks"],
    queryFn: async () => {
      try {
        return await getBanks();
      } catch (error) {
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};
export const useFees = () => {
  return useQuery({
    queryKey: ["fees"],
    queryFn: async () => {
      try {
        const response = (await getFees("zar")) as {
          data?: { data?: unknown };
        };
        return response?.data?.data ?? null;
      } catch (error) {}
      return null;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export interface UserTransactionHistoryPageOptions extends UserTransactionHistoryQuery {
  page?: number;
  limit?: number;
}

interface UserTransactionHistoryListOptions {
  limit?: number;
  asset?: string;
}

interface RawFiatTransaction {
  _id: string;
  userId?: string;
  accountId?: string;
  amount?: { $numberDecimal?: string } | string | number;
  totalAmount?: { $numberDecimal?: string } | string | number;
  type?: string;
  asset?: string;
  status?: string;
  createdAt?: string;
  date?: string;
}

interface PaginatedData {
  results?: unknown[];
  totalPages?: number;
  page?: number;
  currentPage?: number;
}

const normalizeMergedTransactions = (
  transactionResults: unknown[],
  fiatResults: RawFiatTransaction[] = []
) => {
  const mappedFiatResults = fiatResults.map((fiat: RawFiatTransaction) => {
    let amountStr = "0";
    if (fiat.amount) {
      amountStr =
        typeof fiat.amount === "object" && fiat.amount.$numberDecimal
          ? fiat.amount.$numberDecimal
          : String(fiat.amount);
    } else if (fiat.totalAmount) {
      amountStr =
        typeof fiat.totalAmount === "object" && fiat.totalAmount.$numberDecimal
          ? fiat.totalAmount.$numberDecimal
          : String(fiat.totalAmount);
    }

    return {
      _id: fiat._id,
      userId: fiat.userId,
      accountId: fiat.accountId,
      amount: formatCurrencyValue(amountStr, "ZAR", {
        currencyDisplay: "symbol",
      }),
      activity: fiat.type === "Deposit" ? "incoming" : "outgoing",
      asset: fiat.asset?.toLowerCase() || "zar",
      status: fiat.status,
      transactionType: "FiatTransaction",
      transactionSubtype: fiat.type?.toLowerCase() || "deposit",
      transactionRef: fiat._id,
      createdAt: fiat.createdAt || fiat.date,
    };
  });

  const merged = [...transactionResults, ...mappedFiatResults];
  const uniqueResults = merged.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) => (t as { _id?: string })._id === (item as { _id?: string })._id
      )
  );

  uniqueResults.sort(
    (a: unknown, b: unknown) =>
      new Date((b as { createdAt?: string }).createdAt ?? 0).getTime() -
      new Date((a as { createdAt?: string }).createdAt ?? 0).getTime()
  );

  return uniqueResults;
};

export const useBusinessTransactionHistoryPage = (
  businessId: string | null,
  userId: string,
  options: UserTransactionHistoryPageOptions = {},
  enabled = true
) => {
  const activeAccountId = getStoredAccountId() || "";
  const page = Math.max(1, options.page ?? 1);
  const limit = Math.max(1, options.limit ?? TRANSACTION_HISTORY_PAGE_SIZE);

  return useQuery<UserTransactionHistoryPageResult>({
    queryKey: [
      "businessTransactionHistory",
      businessId,
      userId,
      activeAccountId,
      page,
      limit,
      options.asset ?? "",
      options.filterStatus ?? "",
      options.filterType ?? "",
      options.sortBy ?? "",
      options.startDate ?? "",
      options.endDate ?? "",
    ],
    enabled: Boolean(businessId && userId) && enabled,
    queryFn: () =>
      fetchBusinessTransactionHistoryPage(
        userId,
        businessId as string,
        page,
        limit,
        activeAccountId,
        options
      ),
    staleTime: 1000 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

export const useUserTransactionHistoryPage = (
  userId: string,
  options: UserTransactionHistoryPageOptions = {},
  enabled = true
) => {
  const activeAccountId = getStoredAccountId() || "";
  const page = Math.max(1, options.page ?? 1);
  const limit = Math.max(1, options.limit ?? TRANSACTION_HISTORY_PAGE_SIZE);

  return useQuery<UserTransactionHistoryPageResult>({
    queryKey: [
      "userTransactionHistory",
      userId,
      activeAccountId,
      page,
      limit,
      options.asset ?? "",
      options.filterStatus ?? "",
      options.filterType ?? "",
      options.sortBy ?? "",
      options.businessId ?? "",
      options.startDate ?? "",
      options.endDate ?? "",
    ],
    enabled: !!userId && enabled,
    queryFn: () =>
      fetchUserTransactionHistoryPage(userId, page, limit, activeAccountId, {
        ...options,
        accountId: options.businessId ? undefined : activeAccountId,
      }),
    staleTime: 1000 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

export const useUserTransactionHistory = (
  userId: string,
  enabled = true,
  options?: UserTransactionHistoryListOptions | string
) => {
  const activeAccountId = getStoredAccountId() || "";
  const fetchLimit =
    typeof options === "object" && options !== null
      ? (options.limit ?? TRANSACTION_HISTORY_FETCH_LIMIT)
      : TRANSACTION_HISTORY_FETCH_LIMIT;
  const normalizedAsset =
    typeof options === "string"
      ? options.trim().toUpperCase()
      : options?.asset?.trim().toUpperCase();

  return useQuery({
    queryKey: [
      "userTransactionHistory",
      userId,
      activeAccountId,
      normalizedAsset || "all",
      fetchLimit,
    ],
    enabled: !!userId && enabled,
    queryFn: () =>
      fetchAllUserTransactionHistory(userId, activeAccountId, fetchLimit, {
        ...(normalizedAsset ? { asset: normalizedAsset } : {}),
      }),
    staleTime: 1000 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

export const useInfiniteUserTransactionHistory = (
  userId: string,
  enabled = true,
  asset?: string
) => {
  const activeAccountId = getStoredAccountId() || "";
  const normalizedAsset = asset?.trim().toUpperCase();

  return useInfiniteQuery({
    queryKey: [
      "userTransactionHistoryInfinite",
      userId,
      activeAccountId,
      normalizedAsset,
    ],
    enabled: !!userId && enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = Number(pageParam) || 1;
      if (normalizedAsset) {
        const transactionHistoryData = await getUserTransactionHistory(
          userId,
          page,
          activeAccountId,
          undefined,
          { asset: normalizedAsset }
        ).then((res) => (res as { data: { data: PaginatedData } }).data.data);

        return {
          results: normalizeMergedTransactions(
            (transactionHistoryData.results as unknown[]) || [],
            []
          ),
          page:
            transactionHistoryData.currentPage ??
            transactionHistoryData.page ??
            page,
          totalPages: transactionHistoryData.totalPages ?? 1,
        };
      }

      const [transactionHistoryData, fiatTransactionsData] = await Promise.all([
        getUserTransactionHistory(userId, page, activeAccountId).then(
          (res) => (res as { data: { data: PaginatedData } }).data.data
        ),
        page === 1
          ? getMyFiatTransactions(1, activeAccountId)
              .then(
                (res) => (res as { data: { data: PaginatedData } }).data.data
              )
              .catch(() => ({ results: [] }) as PaginatedData)
          : Promise.resolve({ results: [] } as PaginatedData),
      ]);

      return {
        results: normalizeMergedTransactions(
          (transactionHistoryData.results as unknown[]) || [],
          (fiatTransactionsData.results as RawFiatTransaction[]) || []
        ),
        page:
          transactionHistoryData.currentPage ??
          transactionHistoryData.page ??
          page,
        totalPages: transactionHistoryData.totalPages ?? 1,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

interface AvailableAssetsParams {
  isSend?: boolean;
  isEightyeightPay?: boolean;
  investArray?: Record<string, unknown> | null;
  balanceParams?: Record<string, unknown>;
  balanceParamsEightEight?: Record<string, unknown>;
  enabled?: boolean;
  businessId?: string | null;
}

export const useAvailableAssets = ({
  isSend,
  isEightyeightPay,
  investArray,
  balanceParams,
  balanceParamsEightEight,
  enabled = true,
  businessId,
}: AvailableAssetsParams) => {
  const { data: portfolioData } = usePortfolioEstimateAssets(
    businessId ? { businessId } : undefined
  );
  const { selectedCurrency } = useCurrencySelection();

  return useQuery({
    queryKey: [
      "availableAssets",
      isSend,
      isEightyeightPay,
      balanceParams,
      balanceParamsEightEight,
      investArray,
      businessId ?? "personal",
      portfolioData?.timestamp,
      selectedCurrency,
    ],
    enabled: enabled && !!portfolioData,
    queryFn: async () => {
      let availableAmount: V2PortfolioAsset[] = [];

      if (!portfolioData?.assets) {
        return [];
      }

      if (isSend) {
        if (balanceParams?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset: V2PortfolioAsset) =>
              asset.currencyType === balanceParams.type ||
              asset.type === balanceParams.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }

      if (isEightyeightPay) {
        if (balanceParamsEightEight?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset: V2PortfolioAsset) =>
              asset.currencyType === balanceParamsEightEight.type ||
              asset.type === balanceParamsEightEight.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }

      if (!isSend && !isEightyeightPay) {
        availableAmount = portfolioData.assets;
        return availableAmount.length > 0 ? availableAmount : [];
      }

      return [];
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useAvailableBalances = (
  userId: string,
  scope?: WalletQueryScope
) => {
  const scopeBusinessId = scope?.businessId || undefined;
  const { data: portfolioData } = usePortfolioEstimateAssets(
    scopeBusinessId ? { businessId: scopeBusinessId } : undefined
  );
  const { selectedCurrency } = useCurrencySelection();

  return useQuery({
    queryKey: [
      "availableBalances",
      scopeBusinessId || "personal",
      userId,
      portfolioData?.timestamp,
      selectedCurrency,
    ],
    enabled: Boolean(scopeBusinessId || userId) && !!portfolioData,
    queryFn: async () => {
      const metadataResponse = await getAssets();
      const metadataAssets =
        unwrapLegacyEnvelope<
          Array<{
            asset?: string;
            assetName?: string;
            image?: string;
            assetIcon?: string | null;
            _id?: string;
            canBuy?: boolean;
            canSell?: boolean;
            capabilities?: LegacyAsset["capabilities"];
          }>
        >(metadataResponse) ?? [];
      const metadataMap = new Map(
        metadataAssets.map((asset) => [
          String(asset.asset || "").toUpperCase(),
          asset,
        ])
      );
      const assets = portfolioData?.assets || [];

      return assets.map((asset: V2PortfolioAsset) => ({
        ...asset,
        asset: asset.symbol,
        assetId:
          (asset as Record<string, unknown>).assetId ||
          (asset as Record<string, unknown>)._id ||
          metadataMap.get(String(asset.symbol || "").toUpperCase())?._id,
        assetName:
          String(
            (asset as Record<string, unknown>).assetName ||
              metadataMap.get(String(asset.symbol || "").toUpperCase())
                ?.assetName ||
              asset.symbol ||
              ""
          ) || asset.symbol,
        assetIcon:
          metadataMap.get(String(asset.symbol || "").toUpperCase())?.image ||
          metadataMap.get(String(asset.symbol || "").toUpperCase())
            ?.assetIcon ||
          ((asset as Record<string, unknown>).image as string | undefined) ||
          ((asset as Record<string, unknown>).assetIcon as
            | string
            | undefined) ||
          undefined,
        image:
          metadataMap.get(String(asset.symbol || "").toUpperCase())?.image ||
          metadataMap.get(String(asset.symbol || "").toUpperCase())
            ?.assetIcon ||
          ((asset as Record<string, unknown>).image as string | undefined) ||
          ((asset as Record<string, unknown>).assetIcon as string | undefined),
        availableAmountFormatted:
          (asset as V2PortfolioAsset & { forattedAvailableAmount?: string })
            .forattedAvailableAmount ||
          `${asset.available} ${asset.symbol?.toUpperCase()}`,
        assetEstimatedPrice: String(
          deriveAssetEstimatedPrice(asset as Record<string, unknown>)
        ),
        canBuy: metadataMap.get(String(asset.symbol || "").toUpperCase())
          ?.canBuy,
        canSell: metadataMap.get(String(asset.symbol || "").toUpperCase())
          ?.canSell,
        capabilities: metadataMap.get(String(asset.symbol || "").toUpperCase())
          ?.capabilities,
      }));
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
export const useProductHistory = (ledgerId: string) => {
  return useQuery({
    queryKey: ["productTransactionHistory", ledgerId],
    enabled: !!ledgerId,
    queryFn: async () => {
      const response = await getProductTransactionHistory(ledgerId);
      return response;
    },
    staleTime: 1000 * 60 * 2,
  });
};

export const useZar8YieldPositions = (
  params: Record<string, unknown> = {},
  enabled = true,
  businessId?: string | null
) => {
  const scope = businessId || undefined;
  return useQuery({
    queryKey: ["zar8YieldPositions", scope ?? "personal", params],
    queryFn: async () =>
      scope
        ? getBusinessZar8Positions(scope, params)
        : getZar8YieldPositions(params),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
};

export const useZar8YieldPosition = (
  positionId: string,
  enabled = true,
  businessId?: string | null
) => {
  const scope = businessId || undefined;
  return useQuery({
    queryKey: ["zar8YieldPosition", scope ?? "personal", positionId],
    queryFn: async () =>
      scope
        ? getBusinessZar8Position(scope, positionId)
        : getZar8YieldPosition(positionId),
    enabled: enabled && !!positionId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useZar8YieldHistory = (
  params: Record<string, unknown> = {},
  enabled = true,
  businessId?: string | null
) => {
  const scope = businessId || undefined;
  return useQuery({
    queryKey: ["zar8YieldHistory", scope ?? "personal", params],
    queryFn: async () =>
      scope
        ? getBusinessZar8YieldHistory(scope, params)
        : getZar8YieldHistory(params),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
};

export const useZar8YieldEarnings = (
  enabled = true,
  businessId?: string | null
) => {
  const scope = businessId || undefined;
  return useQuery({
    queryKey: ["zar8YieldEarnings", scope ?? "personal"],
    queryFn: async () =>
      scope ? getBusinessZar8YieldEarnings(scope) : getZar8YieldEarnings(),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
};

export const useZar8YieldConfig = (
  enabled = true,
  businessId?: string | null
) => {
  const scope = businessId || undefined;
  return useQuery({
    queryKey: ["zar8YieldConfig", scope ?? "personal"],
    queryFn: async () =>
      scope ? getBusinessZar8YieldConfig(scope) : getZar8YieldConfig(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export const useDepositQuote = (data: Record<string, unknown> | null) => {
  return useQuery({
    queryKey: ["depositQuote", data],
    enabled: !!data,
    queryFn: async () => {
      await createDepositQuote(data ?? {});
      return null;
    },
    staleTime: 1000 * 60 * 2,
  });
};
export const useInvestmentPayWith = (ledgerId: string, assetName: string) => {
  return useQuery({
    queryKey: ["depositQuote", ledgerId, assetName],
    enabled: !!ledgerId && !!assetName,
    queryFn: async () => {
      await getInvestmentPayWith(ledgerId, assetName);
      return null;
    },
    staleTime: 1000 * 60 * 2,
  });
};

export const useInvestProducts = () => {
  return useQuery({
    queryKey: ["investProducts"],
    queryFn: async () => {
      try {
        const data = await getInvestProducts();
        return data ?? [];
      } catch (error) {}
      return [];
    },
    staleTime: 1000 * 60 * 5,
  });
};
const NOTIFICATIONS_POLL_INTERVAL_MS = 1000 * 60;
const ACTIVE_TRANSACTIONS_POLL_INTERVAL_MS = 1000 * 60;

export const useNotifications = (
  page = 1,
  limit = 20,
  category: string | null | undefined = null,
  businessIdOverride?: string | null
) => {
  const { businessId: activeBusinessId } = useActiveBusiness();
  const businessId =
    businessIdOverride === undefined ? activeBusinessId : businessIdOverride;
  return useQuery({
    queryKey: [
      "notifications",
      page,
      limit,
      category,
      businessId ?? "personal",
    ],
    queryFn: async () => {
      try {
        const response = await getNotifications(
          page,
          limit,
          category,
          businessId
        );
        const data =
          (response as { data?: { data?: unknown } })?.data?.data ||
          response ||
          ({} as Record<string, unknown>);

        const rawData = data as {
          notifications?: ReturnType<typeof formatNotification>[];
          results?: ReturnType<typeof formatNotification>[];
          total?: number;
          totalCount?: number;
          unreadCount?: number;
          totalUnread?: number;
          page?: number;
          limit?: number;
        };

        const sourceNotifications =
          rawData.results ?? rawData.notifications ?? [];
        const notifications = sourceNotifications.map(formatNotification);

        return {
          results: notifications,
          totalCount:
            rawData.totalCount ?? rawData.total ?? notifications.length,
          totalUnread: rawData.totalUnread ?? rawData.unreadCount ?? 0,
          page: rawData.page || page,
          limit: rawData.limit || limit,
        };
      } catch (error) {
        console.error("Error fetching notifications:", error);
        return { results: [], totalCount: 0, totalUnread: 0 };
      }
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });
};

export const useNotificationsInfinite = (
  limit = 20,
  category: string | null | undefined = null,
  businessIdOverride?: string | null
) => {
  const { businessId: activeBusinessId } = useActiveBusiness();
  const businessId =
    businessIdOverride === undefined ? activeBusinessId : businessIdOverride;
  return useInfiniteQuery({
    queryKey: [
      "notifications",
      "infinite",
      limit,
      category,
      businessId ?? "personal",
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await getNotifications(
          pageParam,
          limit,
          category,
          businessId
        );
        const data =
          (response as { data?: { data?: unknown } })?.data?.data ||
          response ||
          ({} as Record<string, unknown>);

        const rawData = data as {
          notifications?: ReturnType<typeof formatNotification>[];
          results?: ReturnType<typeof formatNotification>[];
          total?: number;
          totalCount?: number;
          unreadCount?: number;
          totalUnread?: number;
          page?: number;
          limit?: number;
          hasMore?: boolean;
        };

        const sourceNotifications =
          rawData.results ?? rawData.notifications ?? [];
        const notifications = sourceNotifications.map(formatNotification);
        const currentPage = rawData.page ?? pageParam;
        const pageSize = rawData.limit ?? limit;
        const totalCount =
          rawData.totalCount ?? rawData.total ?? notifications.length;

        return {
          results: notifications,
          totalCount,
          totalUnread: rawData.totalUnread ?? rawData.unreadCount ?? 0,
          page: currentPage,
          limit: pageSize,
          hasMore: rawData.hasMore ?? currentPage * pageSize < totalCount,
        };
      } catch (error) {
        console.error("Error fetching notifications:", error);
        return {
          results: [],
          totalCount: 0,
          totalUnread: 0,
          page: pageParam,
          limit,
          hasMore: false,
        };
      }
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 30,
    refetchOnMount: "always",
  });
};
export const useLedgerEntries = (userId: string) => {
  return useQuery({
    queryKey: ["ledgerEntries", userId],
    queryFn: async () => {
      try {
        const response = await getLedgerEntries();
        return response
          ? {
              ...response,
              results: response.investments || [],
            }
          : {
              investments: [],
              results: [],
              totalInvested: 0,
              totalReturns: 0,
              totalValue: 0,
              portfolioEstimate: 0,
              allocations: [],
            };
      } catch (error) {}
      return {
        investments: [],
        results: [],
        totalInvested: 0,
        totalReturns: 0,
        totalValue: 0,
        portfolioEstimate: 0,
        allocations: [],
      };
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const usePendingLedgers = (userId: string) => {
  return useQuery({
    queryKey: ["pendingLedgers", userId],
    queryFn: async () => {
      try {
        const response = await getPendingLedgers();
        return response
          ? {
              ...response,
              results: response.applications || [],
            }
          : {
              applications: [],
              results: [],
              total: 0,
              page: 1,
              limit: 100,
            };
      } catch (error) {}
      return {
        applications: [],
        results: [],
        total: 0,
        page: 1,
        limit: 100,
      };
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const useInvestmentEntry = (ledgerId: string) => {
  return useQuery({
    queryKey: ["invetmentEntries", ledgerId],
    queryFn: async () => {
      try {
        return await getSingleInvestment(ledgerId);
      } catch (error) {}
      return null;
    },
    enabled: !!ledgerId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
export const useInvestmentBalance = (ledgerId: string) => {
  return useQuery({
    queryKey: ["investmentBalance", ledgerId],
    queryFn: async () => {
      try {
        return await getAvalableAmount(ledgerId);
      } catch (error) {}
      return { availableAmount: 0 };
    },
    enabled: !!ledgerId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useInvestPortfolio = (userId: string) => {
  return useQuery({
    queryKey: ["investPortfolio", userId],
    queryFn: async () => {
      const response = await getInvestPortfolio();
      return response;
    },
    staleTime: 1000 * 60 * 3,
    enabled: !!userId,
  });
};

export const useOrders = (userId: string) => {
  return useQuery({
    queryKey: ["userOrders", userId],
    enabled: !!userId,
    queryFn: async () => {
      const response = await getUserOrders();
      return response?.data?.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });
};
export const useCryptoAddresses = () => {
  const { businessId } = useActiveBusiness();
  return useQuery({
    queryKey: ["cryptoAddress", businessId ?? null],
    queryFn: async () => {
      const response = await getWalletAddresses({ businessId });
      return Array.isArray(response) ? response : [];
    },
    staleTime: 1000 * 60 * 2,
  });
};

export const useAssetEstimate = (userId: string, asset: LegacyAsset | null) => {
  const activeAccountId = getStoredAccountId() || "";

  return useQuery({
    queryKey: ["assetEstimate", userId, asset?.asset, activeAccountId],
    enabled: !!userId && !!asset,
    queryFn: async () => {
      const response = await getPortfolio({
        accountId: activeAccountId || undefined,
      });
      return (response as { data?: { data?: unknown } })?.data?.data;
    },
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export const useOrganisations = () => {
  return useQuery({
    queryKey: ["donationOrganisation"],
    queryFn: async () => {
      try {
        const response = (await get(`/api/v2/content/donation-recipients`)) as
          | unknown[]
          | {
              results?: unknown;
              data?: { results?: unknown };
            };

        if (Array.isArray(response)) {
          return response;
        }

        return response?.results || response?.data?.results || [];
      } catch (error) {}
      return [];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useNetworksByAssets = (
  tradingAssets: LegacyAsset[] | null,
  setLoading: (loading: boolean) => void
) => {
  return useQuery({
    queryKey: [
      "networksByAssets",
      tradingAssets?.map((a: LegacyAsset) => a.assetId || a._id),
    ],
    enabled: !!tradingAssets?.length,
    queryFn: async () => {
      try {
        const { getAssetChains } =
          await import("../../services/tradingDataService");

        const assetIds = (tradingAssets ?? []).map(
          (asset: LegacyAsset) => asset.assetId || asset._id
        );

        const chainGroups = await Promise.all(
          assetIds.map((assetId) => getAssetChains(assetId))
        );
        const filteredChains = chainGroups.flat();

        return filteredChains;
      } catch (error) {
        console.error("Error fetching networks:", error);
        return [];
      }
    },
  });
};

interface SelectedNetwork {
  assetId?: string;
  _id?: string;
  [key: string]: unknown;
}

export const useWalletByNetwork = (
  selectedNetwork: SelectedNetwork | null,
  onWalletSelect: (wallet: unknown) => void,
  setLoadingAsset: (loading: boolean) => void
) => {
  return useQuery({
    queryKey: [
      "walletByNetwork",
      selectedNetwork?.assetId,
      selectedNetwork?._id,
    ],
    queryFn: async () => {
      if (!selectedNetwork?.assetId || !selectedNetwork?._id) {
        throw new Error("Invalid network data");
      }

      const walletData = {
        assetId: selectedNetwork.assetId,
        chainId: selectedNetwork._id,
      };

      const response = await getAssetWallet(walletData);
      return unwrapLegacyEnvelope<Record<string, unknown>>(response) ?? null;
    },
    enabled: !!selectedNetwork?.assetId && !!selectedNetwork?._id,
  });
};

export const useFetchPairs = (assetId: string) => {
  return useQuery({
    queryKey: ["assetPairs", assetId],
    queryFn: async () => {
      try {
        const data = await getAssetPairs(assetId);
        return data || [];
      } catch (err) {
        console.error("Error fetching pairs:", err);
        return [];
      }
    },
    enabled: !!assetId,
    staleTime: 1000 * 60 * 5,
  });
};
export const useCryptoAssets = () => {
  return useQuery({
    queryKey: ["cryptoAssets"],
    queryFn: async () => {
      try {
        const data = await getAllTradingAssets("Crypto");
        return data?.data || [];
      } catch (error) {
        console.error("Error fetching crypto assets:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    structuralSharing: true,
  });
};

export const useAllTradingData = (type: string | null = null) => {
  return useQuery({
    queryKey: ["allTradingData", type],
    queryFn: async () => {
      try {
        return await getAllTradingData(type);
      } catch (error) {
        console.error("Error fetching trading data:", error);
        return { tradingAssets: null, walletAssets: null };
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMarketAssets = () => {
  return useQuery({
    queryKey: ["marketAssets"],
    queryFn: async () => {
      const data = await getMarketAssets();
      return data.data ?? {};
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    retryDelay: 1000,
  });
};

export const useRate = (symbol: string) => {
  return useQuery({
    queryKey: ["rate", symbol],
    queryFn: async () => getRate(symbol),
    enabled: Boolean(symbol),
    staleTime: 1000 * 60 * 2,
    retry: 2,
    retryDelay: 1000,
  });
};

export const usePriceHistory = (symbol: string, interval: string) => {
  return useQuery({
    queryKey: ["priceHistory", symbol, interval],
    queryFn: async () => {
      const data = await getPriceHistory(symbol, interval);
      return data?.data?.data ?? null;
    },
    enabled: Boolean(symbol),
    staleTime: 1000 * 60 * 2,
  });
};
export const useAllOwnedAssets = () => {
  return useQuery({
    queryKey: ["allOwnedAssets"],
    queryFn: async () => {
      try {
        const data = await getWalletAssetsData();
        return unwrapLegacyEnvelope<V2PortfolioAsset[]>(data) ?? [];
      } catch (error) {
        console.error("Error fetching owned assets:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useTradeRate = (
  selectedAssetId: string | null,
  action: string | null,
  pair: string | null,
  isPostTrade: boolean
) => {
  const normalizedPostTrade = isPostTrade === true;

  return useQuery({
    queryKey: ["tradeRate", selectedAssetId, action, pair, normalizedPostTrade],
    queryFn: async () => {
      const data = await getTradeRate(
        selectedAssetId ?? "",
        action ?? undefined,
        pair ?? undefined,
        normalizedPostTrade
      );

      return data.data.data;
    },
    enabled:
      selectedAssetId !== undefined &&
      selectedAssetId !== null &&
      action !== undefined &&
      action !== null &&
      pair !== undefined &&
      pair !== null,
    refetchInterval: 30000,
    staleTime: 10000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    networkMode: "online",
    structuralSharing: true,
  });
};

export const useFetchAllTradablePairs = () => {
  const { data: cryptoAssets, isSuccess: isCryptoSuccess } = useCryptoAssets();

  return useQuery({
    queryKey: ["allTradablePairs"],
    queryFn: async () => {
      try {
        return await getAllTradablePairsOptimized();
      } catch (err) {
        console.error("Error fetching all tradable pairs:", err);
        return {};
      }
    },
    enabled: isCryptoSuccess && !!cryptoAssets && cryptoAssets.length > 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useWalletBalance = (scope?: WalletQueryScope) => {
  const activeAccountId = getStoredAccountId() || "";
  const scopeBusinessId = scope?.businessId || undefined;
  const scopeAccountId = scopeBusinessId
    ? undefined
    : activeAccountId || undefined;
  const canLoadPortfolio = useCanLoadPortfolioEstimate();
  const { selectedCurrency } = useCurrencySelection();

  return useQuery({
    queryKey: [
      "portfolioEstimateAssets",
      "balance",
      scopeBusinessId || "personal",
      scopeAccountId || "default",
      selectedCurrency,
    ],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets({
        accountId: scopeAccountId,
        businessId: scopeBusinessId,
        currency: selectedCurrency,
      });
      return response || null;
    },
    enabled: canLoadPortfolio,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error: unknown) => {
      if (
        (error as { response?: { status?: number } })?.response?.status === 401
      ) {
        return false;
      }
      return failureCount < 2;
    },
    networkMode: "online",
    structuralSharing: true,
    select: (data: unknown) => {
      const portfolioData =
        normalizePortfolioPayload(
          unwrapLegacyEnvelope<V2Portfolio>(data),
          selectedCurrency
        ) || ({} as V2Portfolio);
      return {
        usdtEstimate: parseNumericLike(
          (portfolioData as Record<string, unknown>)?.usdtEstimate
        ),
        zarEstimate: parseNumericLike(
          (portfolioData as Record<string, unknown>)?.zarEstimate
        ),
        portfolioEstimate:
          (portfolioData as Record<string, unknown>)?.portfolioEstimate ||
          formatDisplayFiat(0, selectedCurrency),
        portfolioEstimateRaw: parseNumericLike(
          (portfolioData as Record<string, unknown>)?.portfolioEstimateRaw
        ),
        assets: portfolioData?.assets || [],
        timestamp: (portfolioData as Record<string, unknown>)?.timestamp,
      };
    },
  });
};

export const useSubAccounts = (_userId?: string) => {
  const { user, loading } = useAuth();
  const data = useMemo<SubAccount[]>(() => {
    const details = user?.subAccountDetails;
    return Array.isArray(details) ? (details as unknown as SubAccount[]) : [];
  }, [user?.subAccountDetails]);

  return {
    data,
    isLoading: loading && !user,
    isFetching: false,
    isError: false,
    error: null,
    refetch: () => Promise.resolve({ data }),
  };
};

export const useActiveAccount = (_userId: string, accountId: string) => {
  const { user, loading } = useAuth();
  const activeAccount = useMemo<SubAccount | null>(() => {
    const details = (user?.subAccountDetails ?? []) as unknown as SubAccount[];
    if (!Array.isArray(details) || details.length === 0) return null;
    const requested = details.find((acc) => getAccountId(acc) === accountId);
    if (requested) return requested;
    const main = details.find((acc) => acc.isMainAccount);
    return main || details[0] || null;
  }, [user?.subAccountDetails, accountId]);

  return {
    data: activeAccount,
    isLoading: loading && !user,
    isFetching: false,
    isError: false,
    error: null,
    refetch: () => Promise.resolve({ data: activeAccount }),
  };
};
export const usePortfolioEstimateAssets = (scope?: WalletQueryScope) => {
  const activeAccountId = getStoredAccountId() || "";
  const scopeBusinessId = scope?.businessId || undefined;
  const scopeAccountId = scopeBusinessId
    ? undefined
    : activeAccountId || undefined;
  const canLoadPortfolio = useCanLoadPortfolioEstimate();
  const { selectedCurrency } = useCurrencySelection();

  const { data: fullResponse, ...rest } = useQuery({
    queryKey: [
      "portfolioEstimateAssets",
      scopeBusinessId || "personal",
      scopeAccountId || "default",
      selectedCurrency,
    ],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets({
        accountId: scopeAccountId,
        businessId: scopeBusinessId,
        currency: selectedCurrency,
      });
      return response || null;
    },
    enabled: canLoadPortfolio,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error: unknown) => {
      if (
        (error as { response?: { status?: number } })?.response?.status === 401
      ) {
        return false;
      }
      return failureCount < 2;
    },
    select: (data: unknown) =>
      normalizePortfolioPayload(
        unwrapLegacyEnvelope<V2Portfolio>(data),
        selectedCurrency
      ),
    networkMode: "online",
    structuralSharing: true,
  });

  return { data: fullResponse, ...rest };
};

export const useWalletAssets = (scope?: WalletQueryScope) => {
  const { data: portfolioData } = usePortfolioEstimateAssets(scope);
  const activeAccountId = getStoredAccountId() || "";
  const scopeBusinessId = scope?.businessId || undefined;
  const scopeAccountId = scopeBusinessId
    ? undefined
    : activeAccountId || undefined;
  const { selectedCurrency } = useCurrencySelection();

  return useQuery({
    queryKey: [
      "usableAssets",
      scopeBusinessId || "personal",
      scopeAccountId || "default",
      selectedCurrency,
      portfolioData?.timestamp,
    ],
    queryFn: async () => {
      try {
        const assetsMetadata = await getAssets();
        const assetsMetadataList = (unwrapLegacyEnvelope<
          Array<{
            asset?: string;
            image?: string;
            assetIcon?: string | null;
            assetName?: string;
            _id?: string;
            canBuy?: boolean;
            canSell?: boolean;
            capabilities?: LegacyAsset["capabilities"];
          }>
        >(assetsMetadata) ?? []) as Array<{
          asset?: string;
          image?: string;
          assetIcon?: string | null;
          assetName?: string;
          _id?: string;
          canBuy?: boolean;
          canSell?: boolean;
          capabilities?: LegacyAsset["capabilities"];
        }>;

        const assetImageMap = new Map<string, string>();
        const assetNameMap = new Map<string, string>();
        const assetIdMap = new Map<string, string>();
        const assetCapabilitiesMap = new Map<
          string,
          {
            canBuy?: boolean;
            canSell?: boolean;
            capabilities?: LegacyAsset["capabilities"];
          }
        >();
        assetsMetadataList.forEach((asset) => {
          if (asset.asset && (asset.image || asset.assetIcon)) {
            assetImageMap.set(
              asset.asset.toUpperCase(),
              asset.image || asset.assetIcon || ""
            );
          }

          if (asset.asset && asset.assetName) {
            assetNameMap.set(asset.asset.toUpperCase(), asset.assetName);
          }

          if (asset.asset && asset._id) {
            assetIdMap.set(asset.asset.toUpperCase(), asset._id);
          }

          if (asset.asset) {
            assetCapabilitiesMap.set(asset.asset.toUpperCase(), {
              canBuy: asset.canBuy,
              canSell: asset.canSell,
              capabilities: asset.capabilities,
            });
          }
        });

        const portfolioAssets =
          (portfolioData as V2Portfolio | null)?.assets || [];
        const assetsForUse = portfolioAssets.map((asset: V2PortfolioAsset) => {
          const assetKey = asset.symbol?.toUpperCase();
          const assetRecord = asset as V2PortfolioAsset &
            Record<string, unknown>;
          return {
            id:
              assetRecord.assetId ||
              assetIdMap.get(assetKey ?? "") ||
              asset.symbol ||
              assetRecord.assetName,
            asset: asset.symbol,
            assetName:
              String(
                assetRecord.assetName || assetNameMap.get(assetKey ?? "")
              ) || asset.symbol,
            index:
              typeof assetRecord.index === "number" &&
              Number.isFinite(assetRecord.index)
                ? assetRecord.index
                : undefined,
            assetIcon:
              String(
                assetImageMap.get(assetKey ?? "") ||
                  assetRecord.image ||
                  assetRecord.assetIcon ||
                  ""
              ) || undefined,
            image:
              String(
                assetImageMap.get(assetKey ?? "") ||
                  assetRecord.image ||
                  assetRecord.assetIcon ||
                  ""
              ) || "",
            availableAmount: asset.available,
            forattedAvailableAmount: assetRecord.forattedAvailableAmount,
            valueInUsdt: assetRecord.valueInUsdt,
            valueInZar: assetRecord.valueInZar,
            valueInLocalCurrency: assetRecord.valueInLocalCurrency,
            currencySymbol: assetRecord.currencySymbol,
            currencyType: assetRecord.currencyType || asset.type,
            type: asset.type || assetRecord.currencyType,
            assetEstimatedPrice: deriveAssetEstimatedPrice(assetRecord),
            buyRate: assetRecord.buyRate,
            sellRate: assetRecord.sellRate,
            canBuy: assetCapabilitiesMap.get(assetKey ?? "")?.canBuy,
            canSell: assetCapabilitiesMap.get(assetKey ?? "")?.canSell,
            capabilities: assetCapabilitiesMap.get(assetKey ?? "")
              ?.capabilities,
          };
        });

        const pd = portfolioData as
          | (V2Portfolio & Record<string, unknown>)
          | null;
        return {
          assets: assetsForUse as unknown as WalletAsset[],
          usdtEstimate: pd?.usdtEstimate || 0,
          zarEstimate: pd?.zarEstimate || 0,
          portfolioEstimate:
            pd?.portfolioEstimate || formatDisplayFiat(0, selectedCurrency),
          portfolioEstimateRaw: pd?.portfolioEstimateRaw || 0,
          timestamp: pd?.timestamp,
        };
      } catch (err) {
        console.error("Error fetching wallet assets:", err);
        try {
          const data = await getAssets();
          const fallbackAssets: WalletAsset[] = (data?.data?.data ?? []).map(
            (asset) => ({
              assetId: asset.assetId || asset._id,
              asset: asset.asset,
              assetName: asset.assetName,
              image: asset.image || asset.assetIcon || "",
              assetIcon: asset.assetIcon,
              currencyType: asset.type,
              type: asset.type,
            })
          );
          return {
            assets: fallbackAssets,
            usdtEstimate: 0,
            zarEstimate: 0,
            portfolioEstimate: formatDisplayFiat(0, selectedCurrency),
            portfolioEstimateRaw: 0,
          };
        } catch (fallbackErr) {
          throw fallbackErr;
        }
      }
    },
    enabled: !!portfolioData,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    structuralSharing: true,
  });
};

export const useQueryUserBalance = (
  authId: string,
  initialData: { data?: { data?: V2Portfolio } } | null
) => {
  const activeAccountId = getStoredAccountId() || "";
  const canLoadPortfolio = useCanLoadPortfolioEstimate();
  const { selectedCurrency } = useCurrencySelection();

  const { data, ...rest } = useQuery({
    queryKey: [
      "portfolioEstimateAssets",
      "queryUserBalance",
      activeAccountId || "default",
      selectedCurrency,
    ],
    queryFn: async () => {
      const response = await getPortfolioEstimateAssets({
        accountId: activeAccountId || undefined,
        currency: selectedCurrency,
      });
      return response || null;
    },
    enabled: canLoadPortfolio,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 5,
    retry: (failureCount, error: unknown) => {
      if (
        (error as { response?: { status?: number } })?.response?.status === 401
      ) {
        return false;
      }
      return failureCount < 2;
    },
    networkMode: "online",
    structuralSharing: true,
    initialData,
  });
  const balance = data || initialData;
  return { balance, ...rest };
};

export const useQueryDigitalWallet = (
  authId?: string,
  initialData?: unknown,
  businessId?: string | null
) => {
  const activeAccountId = getStoredAccountId() || "";
  const canLoadPortfolio = useCanLoadPortfolioEstimate();
  const { selectedCurrency } = useCurrencySelection();
  const scopedBusinessId = businessId || undefined;
  const scopedAccountId = scopedBusinessId ? undefined : activeAccountId;
  const { data: response, ...rest } = useQuery({
    queryKey: [
      "currencies",
      scopedBusinessId || "personal",
      authId,
      scopedAccountId,
      selectedCurrency,
    ],
    queryFn: () => {
      return getPortfolioEstimateAssets({
        accountId: scopedAccountId || undefined,
        businessId: scopedBusinessId,
        currency: selectedCurrency,
      });
    },
    enabled: canLoadPortfolio,
    staleTime: 15000,
  });

  const { digitalWalletData, defaultFiat } = useMemo(() => {
    const portfolio = normalizePortfolioPayload(
      unwrapLegacyEnvelope<V2Portfolio>(response),
      selectedCurrency
    ) as (V2Portfolio & Record<string, unknown>) | null;

    if (!portfolio) {
      return { digitalWalletData: initialData, defaultFiat: initialData };
    }

    const allAssets = portfolio.assets || [];
    const crypto = allAssets.filter(
      (asset) => asset.currencyType === "Crypto" || asset.type === "crypto"
    );
    const fiat = allAssets.filter(
      (asset) => asset.currencyType === "Fiat" || asset.type === "fiat"
    );

    return {
      digitalWalletData: {
        balances: {
          assetBalances: {
            crypto,
            fiat,
          },
          portfolioEstimate:
            portfolio.portfolioEstimate ||
            formatDisplayFiat(0, selectedCurrency),
        },
        zarEstimate: parseNumericLike(portfolio.zarEstimate),
        usdtEstimate: parseNumericLike(portfolio.usdtEstimate),
        portfolioEstimateRaw: parseNumericLike(portfolio.portfolioEstimateRaw),
        timestamp: portfolio.timestamp,
      },
      defaultFiat:
        fiat.find(
          (asset) =>
            asset.symbol?.toUpperCase() === selectedCurrency ||
            String((asset as Record<string, unknown>).asset ?? "")
              .toUpperCase()
              .trim() === selectedCurrency
        ) ||
        fiat[0] ||
        createEmptyFiatPortfolioAsset(selectedCurrency),
    };
  }, [response, initialData, selectedCurrency]);
  return { digitalWalletData, defaultFiat, ...rest };
};

export const useQueryUserProfile = (initialData: unknown) => {
  const { data, ...rest } = useQuery({
    queryKey: ["userProfile"],
    queryFn: () => getUser(),
    staleTime: 15000,
  });
  const user = data || initialData;
  return { user, ...rest };
};

export const useQueryTradingAssets = (authId: string, initialData: unknown) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["tradingAssets", authId],
    queryFn: ({ queryKey }) => {
      const [, authId] = queryKey;
      return getTradingAssets(authId);
    },
    staleTime: 15000,
  });
  const tradingAssets = data || initialData;
  return { tradingAssets, isLoading, ...rest };
};

export const useQueryWalletAssets = (authId: string, initialData: unknown) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["walletAssets", authId],
    queryFn: () => {
      return getWalletAssets();
    },
    staleTime: 15000,
  });
  const assets = data || initialData;
  return { assets, isLoading, ...rest };
};

export const useQueryTransactionHistory = (
  authId: string,
  initialData: unknown
) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["transactionHistory", authId],
    queryFn: () => {
      return getTransactionHistory();
    },
    staleTime: 15000,
  });
  const transactionHistory = data || initialData;
  return { transactionHistory, isLoading, ...rest };
};

export const useQueryAssetsWithChains = (
  type: string | null,
  initialData: unknown
) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: ["assetsWithChains", type],
    queryFn: () => getAssetsWithChains(type),
    staleTime: 15000,
  });
  const assets = data || initialData;
  return { assets, isLoading, ...rest };
};

export const useQueryTradeRate = (
  selectedCrypto: { assetId?: string; [key: string]: unknown },
  actionLabel: string,
  pair: string,
  initialData: unknown
) => {
  const { user } = useAuth();
  const authId = user?.sub || "";

  const queryKey = [
    "queryTradeRate",
    authId,
    selectedCrypto.assetId,
    actionLabel?.toLowerCase(),
    pair,
  ];

  const { data, isLoading, ...rest } = useQuery({
    queryKey,
    queryFn: ({ queryKey: qk }: { queryKey: readonly unknown[] }) => {
      const [, , assetId, tradeType, qPair] = qk as [
        string,
        string,
        string | undefined,
        string | undefined,
        string,
      ];
      return getTradeRate(assetId ?? "", tradeType, qPair);
    },
    staleTime: 15000,
  });
  const tradeRate = data ? (data as { rate?: unknown }).rate : initialData;
  return { tradeRate, isLoading, ...rest };
};

export const useQueryMutation = (
  mutationFn: (variables: unknown) => Promise<unknown>,
  key: string | string[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: Array.isArray(key) ? key : [key],
        refetchType: "all",
      });
    },
  });
};

const AUTOSTACK_DEFAULT_PURCHASE_LIMIT = 100;

export const useAutoStackStats = () => {
  const { businessId, isLoading: isBusinessContextLoading } =
    useActiveBusiness();
  return useQuery({
    queryKey: ["autoStackStats", businessId ?? "personal"],
    queryFn: async () => {
      return getAutoStackStats(businessId);
    },
    enabled: !isBusinessContextLoading,
    staleTime: 1000 * 60,
  });
};

export const useAutoStackTemplates = () => {
  return useQuery({
    queryKey: ["autoStackTemplates"],
    queryFn: async () => {
      return getAutoStackTemplates();
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useUserAutoStacks = () => {
  const { businessId, isLoading: isBusinessContextLoading } =
    useActiveBusiness();
  return useQuery({
    queryKey: ["userAutoStacks", businessId ?? "personal"],
    queryFn: async () => {
      try {
        const response = await getUserAutoStacks(
          undefined,
          undefined,
          undefined,
          businessId
        );
        return response;
      } catch (error) {
        throw error;
      }
    },
    enabled: !isBusinessContextLoading,
    staleTime: 1000 * 60,
  });
};

export const useUserSingleAutoStack = (stackId: string) => {
  const { businessId, isLoading: isBusinessContextLoading } =
    useActiveBusiness();
  return useQuery({
    queryKey: ["userSingleAutoStack", stackId, businessId ?? "personal"],
    queryFn: async () => {
      const response = await getUserSingleAutoStack(stackId, businessId);
      return response;
    },
    enabled: !!stackId && !isBusinessContextLoading,
    staleTime: 1000 * 30,
  });
};

export const useAutoStackPurchases = (
  stackId: string,
  page = 1,
  limit = AUTOSTACK_DEFAULT_PURCHASE_LIMIT
) => {
  const { businessId, isLoading: isBusinessContextLoading } =
    useActiveBusiness();
  return useQuery({
    queryKey: [
      "autoStackPurchases",
      stackId,
      page,
      limit,
      businessId ?? "personal",
    ],
    queryFn: async () => {
      try {
        const response = await getAutoStackPurchases(
          stackId,
          page,
          limit,
          businessId
        );
        return response;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!stackId && !isBusinessContextLoading,
    staleTime: 1000 * 30,
  });
};

export const useSingleAutoStackStats = (stackId: string) => {
  const { businessId, isLoading: isBusinessContextLoading } =
    useActiveBusiness();
  const stackQuery = useUserSingleAutoStack(stackId);
  const allPurchasesQuery = useQuery({
    queryKey: ["autoStackAllPurchases", stackId, businessId ?? "personal"],
    queryFn: () => getAllAutoStackPurchases(stackId, businessId),
    enabled: !!stackId && !isBusinessContextLoading,
    staleTime: 1000 * 30,
  });
  const marketQuery = useMarketAssets();

  const data = useMemo(() => {
    if (!stackQuery.data || !allPurchasesQuery.data || !marketQuery.data) {
      return undefined;
    }
    return computeSingleAutoStackStats(
      stackQuery.data,
      allPurchasesQuery.data,
      marketQuery.data as Record<
        string,
        { priceInZar?: number; price?: number }
      >
    );
  }, [stackQuery.data, allPurchasesQuery.data, marketQuery.data]);

  return {
    data,
    isLoading:
      isBusinessContextLoading ||
      stackQuery.isLoading ||
      allPurchasesQuery.isLoading ||
      marketQuery.isLoading,
    isFetching:
      stackQuery.isFetching ||
      allPurchasesQuery.isFetching ||
      marketQuery.isFetching,
    error:
      stackQuery.error || allPurchasesQuery.error || marketQuery.error || null,
  };
};

export const useBankTransferReference = () => {
  return useQuery({
    queryKey: ["bankTransferReference"],
    queryFn: async () => {
      // The backend requires a bank-account id on the reference
      // endpoint — resolve the user's primary (or first) account before
      // calling, mirroring the previous default-bank behaviour.
      const bankAccountId = await resolveDefaultBankAccountId();
      if (!bankAccountId) {
        return null;
      }
      const response = await getBankTransferReference(bankAccountId);
      return (
        (response as { data?: { data?: unknown } })?.data?.data ?? response
      );
    },
  });
};

export const useActiveTransactions = () => {
  const setActiveTransactions = useTransactionTrackingStore(
    (s) => s.setActiveTransactions
  );
  const activeAccountId = getStoredAccountId() ?? undefined;

  const query = useQuery({
    queryKey: ["activeTransactions", activeAccountId ?? "default"],
    queryFn: async () => {
      const response = await getNormalizedActiveTransactions(activeAccountId);
      return (
        unwrapLegacyEnvelope<Array<{ _id: string; [key: string]: unknown }>>(
          response
        ) ?? []
      );
    },
    refetchInterval: ACTIVE_TRANSACTIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10000,
  });

  useEffect(() => {
    if (query.data) {
      setActiveTransactions(query.data);
    } else if (query.isError) {
      setActiveTransactions([]);
    }
  }, [query.data, query.isError, setActiveTransactions]);

  return query;
};

export const useDismissTransaction = () => {
  const queryClient = useQueryClient();
  const removeTransaction = useTransactionTrackingStore(
    (s) => s.removeTransaction
  );

  return useMutation({
    mutationFn: async (transactionId: string) => {
      return updatePatch(
        `/api/v2/transactions/crypto/${transactionId}/dismiss`
      );
    },
    onSuccess: (_: unknown, transactionId: string) => {
      removeTransaction(transactionId);
      queryClient.invalidateQueries({ queryKey: ["activeTransactions"] });
    },
  });
};

export {
  useUserFeatures,
  useAllFeatureAccess,
  useFeatureAccessByKey,
  useFeatureAccessByCategory,
} from "./featureAccessQueries";
